import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { PrimaryButton } from '../../ui/common';

// Filtry w widgecie (24.10.2026, migracja 068). Firma wybiera CECHY sprzętu, po których klient
// zawęzi listę w widgecie; wartości widget liczy sam z magazynu. Panel pokazuje przy każdej
// cesze, ile ma różnych wartości i jakie, żeby było widać, który filtr ma sens, a pod listą
// podgląd paska filtrów w takim kształcie, w jakim zobaczy go klient.
// API: GET/PUT /api/widget-filters (Login/backend/routes/widgetFilters.js).

const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  overflow: hidden;
`;

const Wiersz = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border-top: 1px solid var(--input-border);
  background: ${(p) => (p.$on ? 'var(--card-bg)' : 'transparent')};
  &:first-child { border-top: 0; }

  @media (max-width: 640px) {
    grid-template-columns: auto minmax(0, 1fr);
    > :last-child { grid-column: 2; justify-self: start; }
  }
`;

const Zaznacz = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
  cursor: pointer;
`;

const Opis = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Nazwa = styled.label`
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PoleEtykiety = styled.input`
  max-width: 260px;
  padding: 7px 10px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.9rem;
  &:focus { outline: 2px solid var(--primary); outline-offset: 1px; }
`;

const Akcje = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MalyPrzycisk = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid var(--input-border);
  border-radius: 7px;
  background: var(--card-bg);
  color: var(--text);
  cursor: pointer;
  &:disabled { opacity: 0.35; cursor: default; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const Plakietka = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 5px;
  background: var(--table-header-bg);
  color: var(--text-muted);
  white-space: nowrap;
`;

// Podgląd paska filtrów: te same metki co w widgecie (widget/src/styles-v2.css, .w-chip), na
// białym tle, bo tak wygląda widget na stronie wypożyczalni.
const Podglad = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px dashed var(--input-border);
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;

  .etykieta { font-size: 0.68rem; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #64748b; margin-bottom: 6px; }
  .metki { display: flex; flex-wrap: wrap; gap: 5px; }
  .metka { padding: 5px 9px; border: 1px solid #d4dce8; border-radius: 6px; font-size: 0.8rem; color: #334155; }
  .zakres { display: flex; gap: 5px; align-items: center; color: #64748b; font-size: 0.8rem; }
  .zakres span.pole { width: 64px; padding: 5px 8px; border: 1px solid #d4dce8; border-radius: 6px; color: #94a3b8; }
`;

const Stopka = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const Komunikat = styled.span`
  font-size: 0.85rem;
  color: ${(p) => (p.$blad ? 'var(--danger)' : 'var(--text-muted)')};
`;

const Rozwin = styled.button`
  margin-top: 8px;
  border: 0;
  background: none;
  padding: 4px 0;
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:hover { color: var(--text); }
`;

const PROG_ZAKRESU = 7; // lustro widget/src/filtryWidgetu.js

export default function WidgetFiltersPanel() {
  const { t } = useTranslation();
  const [kandydaci, setKandydaci] = useState([]);
  const [wybrane, setWybrane] = useState([]); // [{key,label,kind}]
  const [zapisane, setZapisane] = useState('[]');
  const [maks, setMaks] = useState(6);
  const [stan, setStan] = useState('loading'); // loading | ready | error
  const [zapis, setZapis] = useState({ trwa: false, tekst: '', blad: false });
  // Cechy, których żaden sprzęt jeszcze nie ma, są zwinięte: na liście zostaje to, co ma sens dziś.
  const [pokazPuste, setPokazPuste] = useState(false);

  useEffect(() => {
    let anulowane = false;
    fetch(`${API_BASE}/api/widget-filters`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (anulowane) return;
        setKandydaci(Array.isArray(d.candidates) ? d.candidates : []);
        const f = Array.isArray(d.filters) ? d.filters : [];
        setWybrane(f);
        setZapisane(JSON.stringify(f));
        if (d.max) setMaks(d.max);
        setStan('ready');
      })
      .catch(() => { if (!anulowane) setStan('error'); });
    return () => { anulowane = true; };
  }, []);

  const poKluczu = useMemo(() => new Map(kandydaci.map((k) => [k.key, k])), [kandydaci]);
  const zmienione = JSON.stringify(wybrane) !== zapisane;
  // Wybrane w kolejności widgetu, pod nimi reszta: te z wartościami w magazynie najpierw.
  const pozostale = kandydaci
    .filter((k) => !wybrane.some((w) => w.key === k.key))
    .sort((a, b) => (b.distinct >= 2) - (a.distinct >= 2) || b.units - a.units);

  const przelacz = (k) => {
    setZapis({ trwa: false, tekst: '', blad: false });
    setWybrane((obecne) => (obecne.some((w) => w.key === k.key)
      ? obecne.filter((w) => w.key !== k.key)
      : (obecne.length >= maks ? obecne : [...obecne, { key: k.key, label: k.label, kind: 'auto' }])));
  };
  const przesun = (i, o) => setWybrane((obecne) => {
    const j = i + o;
    if (j < 0 || j >= obecne.length) return obecne;
    const kopia = [...obecne];
    [kopia[i], kopia[j]] = [kopia[j], kopia[i]];
    return kopia;
  });
  const etykieta = (key, label) => setWybrane((obecne) => obecne.map((w) => (w.key === key ? { ...w, label } : w)));

  const zapiszFiltry = async () => {
    setZapis({ trwa: true, tekst: '', blad: false });
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/widget-filters`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ filters: wybrane.map((w) => ({ ...w, label: w.label.trim() })) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('widgetFilters.saveError'));
      setWybrane(d.filters);
      setZapisane(JSON.stringify(d.filters));
      setZapis({ trwa: false, tekst: t('widgetFilters.saved'), blad: false });
    } catch (e) {
      setZapis({ trwa: false, tekst: e.message || t('widgetFilters.saveError'), blad: true });
    }
  };

  const meta = (k) => {
    if (!k || !k.units) return t('widgetFilters.noData');
    const przyklady = k.sample.slice(0, 4).join(', ');
    return t('widgetFilters.meta').replace('{n}', String(k.distinct)).replace('{values}', przyklady);
  };
  const rodzaj = (k) => (k && k.numeric && k.distinct >= PROG_ZAKRESU ? 'zakres' : 'przyciski');

  if (stan === 'loading') return <Komunikat>{t('widgetFilters.loading')}</Komunikat>;
  if (stan === 'error') return <Komunikat $blad>{t('widgetFilters.loadError')}</Komunikat>;

  const wiersz = (k, w, i) => {
    const on = !!w;
    const id = `widget-filter-${k.key}`;
    return (
      <Wiersz key={k.key} $on={on} data-cy={`widget-filter-row-${k.key}`}>
        <Zaznacz
          id={id}
          type="checkbox"
          checked={on}
          disabled={!on && wybrane.length >= maks}
          onChange={() => przelacz(k)}
          data-cy={`widget-filter-toggle-${k.key}`}
        />
        <Opis>
          {on ? (
            <PoleEtykiety
              value={w.label}
              maxLength={40}
              onChange={(e) => etykieta(k.key, e.target.value)}
              aria-label={t('widgetFilters.labelAria').replace('{name}', k.label)}
              data-cy={`widget-filter-label-${k.key}`}
            />
          ) : (
            <Nazwa htmlFor={id}>{k.label}</Nazwa>
          )}
          <Meta title={k.sample.join(', ')}>{meta(k)}</Meta>
        </Opis>
        <Akcje>
          {k.distinct > 0 && k.distinct < 2 && <Plakietka>{t('widgetFilters.oneValue')}</Plakietka>}
          {rodzaj(k) === 'zakres' && <Plakietka>{t('widgetFilters.rangeBadge')}</Plakietka>}
          {on && (
            <>
              <MalyPrzycisk type="button" onClick={() => przesun(i, -1)} disabled={i === 0} aria-label={t('widgetFilters.up').replace('{name}', w.label)}>↑</MalyPrzycisk>
              <MalyPrzycisk type="button" onClick={() => przesun(i, 1)} disabled={i === wybrane.length - 1} aria-label={t('widgetFilters.down').replace('{name}', w.label)}>↓</MalyPrzycisk>
            </>
          )}
        </Akcje>
      </Wiersz>
    );
  };

  return (
    <div data-cy="widget-filters">
      <Lista>
        {wybrane.map((w, i) => wiersz(poKluczu.get(w.key) || { key: w.key, label: w.label, sample: [], distinct: 0, units: 0 }, w, i))}
        {pozostale.filter((k) => k.units > 0).map((k) => wiersz(k, null, -1))}
        {pokazPuste && pozostale.filter((k) => !k.units).map((k) => wiersz(k, null, -1))}
      </Lista>
      {pozostale.some((k) => !k.units) && (
        <Rozwin type="button" onClick={() => setPokazPuste((v) => !v)} aria-expanded={pokazPuste} data-cy="widget-filters-show-empty">
          {pokazPuste
            ? t('widgetFilters.hideEmpty')
            : t('widgetFilters.showEmpty').replace('{n}', String(pozostale.filter((k) => !k.units).length))}
        </Rozwin>
      )}

      {wybrane.length > 0 && (
        <Podglad aria-label={t('widgetFilters.previewAria')} data-cy="widget-filters-preview">
          {wybrane.map((w) => {
            const k = poKluczu.get(w.key);
            return (
              <div key={w.key}>
                <div className="etykieta">{w.label || (k && k.label)}</div>
                {rodzaj(k) === 'zakres' ? (
                  <div className="zakres"><span className="pole">{t('widgetFilters.from')}</span>–<span className="pole">{t('widgetFilters.to')}</span></div>
                ) : (
                  <div className="metki">
                    {(k && k.sample.length ? k.sample.slice(0, 4) : ['…']).map((v) => <span className="metka" key={v}>{v}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </Podglad>
      )}

      <Stopka>
        <Komunikat $blad={zapis.blad} role="status">
          {zapis.tekst || t('widgetFilters.counter').replace('{n}', String(wybrane.length)).replace('{max}', String(maks))}
        </Komunikat>
        <PrimaryButton type="button" onClick={zapiszFiltry} disabled={!zmienione || zapis.trwa} data-cy="widget-filters-save">
          {zapis.trwa ? t('common.saving') : t('widgetFilters.save')}
        </PrimaryButton>
      </Stopka>
    </div>
  );
}
